import {ErrorBoundary} from 'react-error-boundary';
import {ERROR, UNIT} from '@di-shared/const';
import React, {Suspense} from 'react';
import {EnergyUsageDailyProps} from '@di-shared/features/history/energy-usage/EnergyUsageDaily/index';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApiEu} from '@di-shared/api/di';
import {EV_DAILY_GRAPH_MAX_LENGTH, useEnergyUsageDaily} from '@di-shared/features/history/energy-usage/useEnergyUsageDaily';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {subDate} from '@shared/dateUtils';
import DailyLineGraphError from '@di-shared/features/common/DailyLineGraphError';
import EnergyPowerDailyGraph from '@di-shared/features/history/energy-usage/EnergyPowerDailyGraph';
import EnergyMileageDailyGraph from '@di-shared/features/history/energy-usage/EnergyMileageDailyGraph';
import {formatNumberWithCommas} from '@di-shared/utils';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

export default function ({vin}: EnergyUsageDailyProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: EnergyUsageDailyProps) {
  const {data: evDailyData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveEvStatsFor30Days), vin],
    queryFn: () => diApiEu.drivingHistory.retrieveEvStatsFor30Days({vin}),
    select: ({data}) => data,
  });
  const {isDailyEnergyUsageNone} = useEnergyUsageDaily({evDailyData});

  // No data 화면
  if (isDailyEnergyUsageNone) {
    return <None />;
  }

  // Default 화면
  return <Default vin={vin} />;
}

function None() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  // No data 세팅

  const evDrivingInfoNone = Array.from({length: EV_DAILY_GRAPH_MAX_LENGTH})
    .map((_, index) => {
      return {
        date: subDate(new Date(), {days: index + 1}),
        powerConsumption: 0,
        avrMileage: 0,
        regenPower: 0,
      };
    })
    .sort((a, b) => +a.date - +b.date);

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_ev_30day_title_02')}
        </h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytirps_updated')} ${CldrDatetimeFormatter.format(evDrivingInfoNone[0].date, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(evDrivingInfoNone[evDrivingInfoNone.length - 1].date, 'yMMMd')}`}
        >
          {t('ctn_mytirps_updated')}
          {`${CldrDatetimeFormatter.format(evDrivingInfoNone[0].date, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(evDrivingInfoNone[evDrivingInfoNone.length - 1].date, 'yMMMd')}`}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <div
            className="device_info_item type_start"
            role="text"
            aria-label={`${t('ctn_mytrips_ev_30day_energyuse')} ${0}${UNIT.KWH} ${t('ctn_mytrips_ev_30day_average')} ${0}${UNIT.KWH}`}
          >
            <h4
              className="device_info_key body_xlarge_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_30day_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {0}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
              <span className="device_info_average label_small_r">
                {t('ctn_mytrips_ev_30day_average')} {0 + UNIT.KWH}
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyPowerDailyGraph
            data={evDrivingInfoNone.map((item) => ({
              score: item.powerConsumption,
              scoreDate: item.date,
            }))}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_max_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{0}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_min_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{0}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <div
            className="device_info_item type_start"
            role="text"
            aria-label={`${t('ctn_mytrips_ev_30day_average_energyuse')} ${0}${defaultEnergyEfficiencySymbol}`}
          >
            <h4
              className="device_info_key body_xlarge_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_30day_average_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {0}
                <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyMileageDailyGraph
            data={evDrivingInfoNone.map((item) => ({
              score: item.avrMileage,
              scoreDate: item.date,
            }))}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_max_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{0}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_min_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{0}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container section_container_small">
        <div
          className="device_info_item"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_regenerative_02')} ${0}${UNIT.KWH}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4
            className="device_info_key body_medium_b"
            aria-hidden={true}
          >
            {t('ctn_mytrips_ev_regenerative_02')}
          </h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {0}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function Default({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertEnergyEfficiencyValue, convertEnergyEfficiencySymbol} = useUnitConverter();
  const {data: evDailyData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveEvStatsFor30Days), vin],
    queryFn: () => diApiEu.drivingHistory.retrieveEvStatsFor30Days({vin}),
    select: ({data}) => data,
  });
  const {verifiedEvDrivingInfoList, avgPowerConsumption, minPowerConsumption, maxPowerConsumption, maxAvrMileage, minAvrMileage} =
    useEnergyUsageDaily({evDailyData});

  const convertedMaxAvrMileageCandidate = convertEnergyEfficiencyValue(maxAvrMileage);
  const convertedMinAvrMileageCandidate = convertEnergyEfficiencyValue(minAvrMileage);

  let convertedMaxAvrMileage = convertedMaxAvrMileageCandidate;
  let convertedMinAvrMileage = convertedMinAvrMileageCandidate;

  if (typeof convertedMaxAvrMileageCandidate === 'number' && typeof convertedMinAvrMileageCandidate === 'number') {
    convertedMaxAvrMileage = Math.max(convertedMaxAvrMileageCandidate, convertedMinAvrMileageCandidate);
    convertedMinAvrMileage = Math.min(convertedMaxAvrMileageCandidate, convertedMinAvrMileageCandidate);
  }

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_ev_30day_title_01')}
        </h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytirps_updated')} ${CldrDatetimeFormatter.format(verifiedEvDrivingInfoList[0].verifiedDate, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(verifiedEvDrivingInfoList[verifiedEvDrivingInfoList.length - 1].verifiedDate, 'yMMMd')}`}
        >
          {t('ctn_mytirps_updated')}&nbsp;
          {`${CldrDatetimeFormatter.format(verifiedEvDrivingInfoList[0].verifiedDate, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(verifiedEvDrivingInfoList[verifiedEvDrivingInfoList.length - 1].verifiedDate, 'yMMMd')}`}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <div
            className="device_info_item type_start"
            role="text"
            aria-label={`${t('ctn_mytrips_ev_30day_energyuse')} ${formatNumberWithCommas(evDailyData.evStatistics.totalPowerConsumption)}${UNIT.KWH} ${t('ctn_mytrips_ev_30day_average')} ${formatNumberWithCommas(avgPowerConsumption)}${UNIT.KWH}`}
          >
            <h4
              className="device_info_key body_xlarge_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_30day_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {formatNumberWithCommas(evDailyData.evStatistics.totalPowerConsumption)}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
              <span className="device_info_average label_small_r">
                {t('ctn_mytrips_ev_30day_average')} {formatNumberWithCommas(avgPowerConsumption) + UNIT.KWH}
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyPowerDailyGraph
            data={verifiedEvDrivingInfoList.map((item) => ({
              score: item.powerConsumption,
              scoreDate: item.verifiedDate,
            }))}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_max_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{formatNumberWithCommas(maxPowerConsumption)}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_min_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{formatNumberWithCommas(minPowerConsumption)}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container">
        <div
          className="section_container_header"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_30day_average_energyuse')} ${formatNumberWithCommas(convertEnergyEfficiencyValue(evDailyData.evStatistics.avrMileage))}${convertEnergyEfficiencySymbol(evDailyData.evStatistics.avrMileage)}`}
        >
          <div className="device_info_item type_start">
            <h4
              className="device_info_key body_xlarge_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_30day_average_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {formatNumberWithCommas(convertEnergyEfficiencyValue(evDailyData.evStatistics.avrMileage))}
                <span className="body_small_r">{convertEnergyEfficiencySymbol(evDailyData.evStatistics.avrMileage)}</span>
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyMileageDailyGraph
            data={verifiedEvDrivingInfoList.map((item) => {
              const result = item.avrMileage;
              return {
                score: typeof result === 'number' ? result : 0,
                scoreDate: item.verifiedDate,
              };
            })}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_max_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{formatNumberWithCommas(convertedMaxAvrMileage)}</span>
              <span className="statistics_info_unit">{convertEnergyEfficiencySymbol(Math.max(maxAvrMileage, minAvrMileage))}</span>
            </span>
          </li>
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_min_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{formatNumberWithCommas(convertedMinAvrMileage)}</span>
              <span className="statistics_info_unit">{convertEnergyEfficiencySymbol(Math.min(maxAvrMileage, minAvrMileage))}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container section_container_small">
        <div
          className="device_info_item"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_regenerative_02')} ${formatNumberWithCommas(evDailyData.evStatistics.totalRegenPower)}${UNIT.KWH}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4
            className="device_info_key body_medium_b"
            aria-hidden={true}
          >
            {t('ctn_mytrips_ev_regenerative_02')}
          </h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {formatNumberWithCommas(evDailyData.evStatistics.totalRegenPower)}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();

  return (
    <section
      className="section"
      aria-hidden="true"
    >
      <div className="section_heading type_main">
        <h3
          className="section_heading_title skeleton"
          style={{width: '160px', height: '20px'}}
        />
        <h3
          className="section_heading_title skeleton"
          style={{width: '200px', height: '28px'}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{width: '160px', height: '20px'}}
        />
      </div>
      <div className="section_container skeleton">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('ctn_mytrips_ev_30day_energyuse')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
              <span className="device_info_average label_small_r">
                {t('ctn_mytrips_ev_30day_average')}
                {ERROR.DASH + UNIT.KWH}
              </span>
            </span>
          </div>
        </div>
        <DailyLineGraphError />
        <ul className="statistics_info_list">
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_max_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_min_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container skeleton">
        <div className="section_container_header">
          <div className="device_info_item type_start">
            <h4 className="device_info_key body_xlarge_b">{t('ctn_mytrips_ev_30day_average_energyuse')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
              </span>
            </span>
          </div>
        </div>
        <DailyLineGraphError />
        <ul className="statistics_info_list">
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_max_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_min_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('ctn_mytrips_ev_regenerative_02')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  // No data 세팅
  const evDrivingInfoNone = Array.from({length: EV_DAILY_GRAPH_MAX_LENGTH})
    .map((_, index) => {
      return {
        date: subDate(new Date(), {days: index + 1}),
        powerConsumption: 0,
        avrMileage: 0,
        regenPower: 0,
      };
    })
    .sort((a, b) => +a.date - +b.date);

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_ev_30day_title_01')}
        </h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytirps_updated')} ${CldrDatetimeFormatter.format(evDrivingInfoNone[0].date, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(evDrivingInfoNone[evDrivingInfoNone.length - 1].date, 'yMMMd')}`}
        >
          {t('ctn_mytirps_updated')}
          {`${CldrDatetimeFormatter.format(evDrivingInfoNone[0].date, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(evDrivingInfoNone[evDrivingInfoNone.length - 1].date, 'yMMMd')}`}
        </span>
      </div>
      <div className="section_container">
        <div
          className="section_container_header"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_30day_energyuse')} ${ERROR.DASH}${UNIT.KWH} ${t('ctn_mytrips_ev_30day_average')} ${ERROR.DASH}${UNIT.KWH}`}
        >
          <div className="device_info_item type_start">
            <h4
              className="device_info_key body_xlarge_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_30day_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
              <span className="device_info_average label_small_r">
                {t('ctn_mytrips_ev_30day_average')} {ERROR.DASH + UNIT.KWH}
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyPowerDailyGraph
            data={evDrivingInfoNone.map((item) => ({
              score: item.powerConsumption,
              scoreDate: item.date,
            }))}
            error={true}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_max_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_min_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{UNIT.KWH}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container">
        <div
          className="section_container_header"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_30day_average_energyuse')} ${ERROR.DASH}${defaultEnergyEfficiencySymbol}`}
        >
          <div className="device_info_item type_start">
            <h4
              className="device_info_key body_xlarge_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_30day_average_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
              </span>
            </span>
          </div>
        </div>
        <ErrorBoundary fallback={<DailyLineGraphError />}>
          <EnergyMileageDailyGraph
            data={evDrivingInfoNone.map((item) => ({
              score: item.avrMileage,
              scoreDate: item.date,
            }))}
            error={true}
          />
        </ErrorBoundary>
        <ul className="statistics_info_list">
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_max_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
          <li
            className="statistics_info_item"
            role="text"
          >
            <span className="statistics_info_key">{t('ctn_mytrips_ev_30day_min_01')}</span>
            <span className="statistics_info_val">
              <span className="statistics_info_number">{ERROR.DASH}</span>
              <span className="statistics_info_unit">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </li>
        </ul>
      </div>
      <div className="section_container">
        <div
          className="device_info_item"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_regenerative_02')} ${ERROR.DASH}${UNIT.KWH}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4
            className="device_info_key body_medium_b"
            aria-hidden={true}
          >
            {t('ctn_mytrips_ev_regenerative_02')}
          </h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}
